import { useEffect, useMemo, useState } from 'react';

import { Route, Routes, useLocation } from 'react-router-dom';
import CustomerDashboard from '../components/dashboard/customers/CustomerDashboard';
import SideNav from '../components/dashboard/Sidenav';
import TopBar from '../components/dashboard/TopBar';
import VendorDashboard from '../components/dashboard/vendors/VendorDashboard';
import { CustomerWithDriverOnboarding, Entity } from '../types/apiTypes';
import { useAuth } from '../utils/AuthContext';
import { useScreenView } from '../utils/ScreenViewContext';
import { useUser } from '../utils/UserContext';
import { useAuthRouter } from '../utils/AuthRouter';
import PartnerDashboard from '../components/dashboard/partners/PartnerDashboard';
import { useQuery } from '@tanstack/react-query';
import useApi from '../utils/AxiosWrapper';
import DriverDashboard from '../components/dashboard/drivers/DriverDashboard';
import { isPreActiveCustomer } from '../components/dashboard/customers/tabs/home/utils/demoData';
import MobileAppPrompt from '../components/MobileAppPrompt';

function Dashboard() {
  // const [currStep, setCurrStep] = useState(0);
  // const { id } = useParams<{ id: string }>();
  // const [selectedRole, setSelectedRole] = useState<Role>({} as Role);
  const { isAuthenticated, isLoadingCached } = useAuth();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState('');
  const [subTab, setSubTab] = useState('Overview');
  const { screenView } = useScreenView();
  const [showSideNav, setShowSideNav] = useState(false);
  const { userRecord, userId, isLoggingOut } = useUser();
  const { callProtectedApi } = useApi();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  useAuthRouter(); // Handle auth routing
  useEffect(() => {
    if (
      userRecord &&
      (userRecord?.status === 'active' ||
        userRecord?.status === 'pre_active') &&
      isAuthenticated
    ) {
      // Extract tab and subtab from the URL
      let loc = location.pathname.replace('/dashboard/', '');
      const pathSegments = loc.split('/'); // Split the URL by '/'

      if (pathSegments.length > 0) {
        let tab = pathSegments[0];
        let sub = pathSegments[1] || ''; // Second part is the sub-tab (if it exists)

        // Format tab and sub-tab to have proper capitalization
        const tabWithDash = tab.split('-');
        if (tabWithDash?.length > 1) {
          tab =
            tabWithDash[0][0]?.toUpperCase() +
            tabWithDash[0]?.slice(1) +
            ' ' +
            tabWithDash[1][0]?.toUpperCase() +
            tabWithDash[1]?.slice(1);
        } else {
          tab = tab[0]?.toUpperCase() + tab?.slice(1);
        }
        if (sub && sub?.length > 0) {
          const subWithDash = sub.split('-');
          if (subWithDash?.length > 1) {
            sub =
              subWithDash[0][0]?.toUpperCase() +
              subWithDash[0]?.slice(1) +
              ' ' +
              subWithDash[1][0]?.toUpperCase() +
              subWithDash[1]?.slice(1);
          } else {
            sub = sub[0]?.toUpperCase() + sub?.slice(1);
          }
        }

        setCurrentTab(tab);
        sub = sub.trim();
        if (sub === '') {
          setSubTab('Overview');
        } else {
          setSubTab(sub);
        }
      }
    }
    // Routing handled by AuthRouter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, userRecord]);

  const {
    data: uncategorizedTransactionsCount = 0,
    isLoading: isLoadingCount,
  } = useQuery<number>({
    queryKey: ['uncategorizedTransactionsCount', userId ?? '', {}],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/transactions/count/${userId ?? ''}/uncategorized`,
        'get',
        {}
      );
      return response?.data?.data?.count || [];
    },
    enabled:
      !!userId && userRecord?.userType === Entity.Customer && !isPreActive,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const handleUpdateShowSideNav = (show: boolean) => {
    setShowSideNav(show);
  };

  const updateCurrentTab = (tab: string) => {
    setCurrentTab(tab);
  };

  const updateSubTab = (sub: string) => {
    setSubTab(sub);
  };

  const props = {
    currentTab,
    setCurrentTab: updateCurrentTab,
    userType: userRecord?.userType ?? '',
    currentPage: location.pathname,
    subTab,
    setSubTab: updateSubTab,
    screenView,
    showSideNav,
    setShowSideNav: handleUpdateShowSideNav,
  };

  // Don't render anything if not authenticated or logging out
  if (!isAuthenticated || isLoggingOut) {
    return (
      <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
        <img
          alt="loading-icon"
          src="/Icon-v2.gif"
          className="h-44 w-44 justify-center"
        />
        <h2 className="text-[14px] text-secondary">
          {isLoggingOut ? 'Logging out...' : 'Redirecting...'}
        </h2>
      </div>
    );
  }

  return (
    <DashboardLayout
      userType={userRecord?.userType || 'customer'}
      currentPage={location.pathname}
      currentTab={currentTab}
      setCurrentTab={updateCurrentTab}
      subTab={subTab}
      setSubTab={updateSubTab}
      showSideNav={showSideNav}
      setShowSideNav={handleUpdateShowSideNav}
      screenView={screenView}
      uncategorizedTransactionsCount={uncategorizedTransactionsCount}
      isLoadingUncategorizedTransactionsCount={isLoadingCount}
    >
      {isLoadingCached || !userRecord ? (
        <div className="mt-[20%] flex h-full w-full flex-col items-center justify-center align-middle">
          <img
            alt="loading-icon"
            src="/Icon-v2.gif"
            className="h-44 w-44 justify-center"
          />
        </div>
      ) : (
        <>
          {isAuthenticated &&
            userRecord?.status &&
            (userRecord?.status === 'active' ||
              userRecord?.status === 'pre_active') &&
            userRecord?.userType === Entity.Customer && (
              <Routes>
                <Route path="/*" element={<CustomerDashboard {...props} />} />
              </Routes>
            )}
          {isAuthenticated &&
            userRecord?.status &&
            userRecord?.status === 'active' &&
            userRecord?.userType === Entity.Vendor && (
              <Routes>
                <Route path="/*" element={<VendorDashboard {...props} />} />
              </Routes>
            )}
          {isAuthenticated &&
            userRecord?.status &&
            userRecord?.status === 'active' &&
            (userRecord?.userType === Entity.Partner ||
              userRecord?.userType === Entity.PartnerAgent) && (
              <Routes>
                <Route path="/*" element={<PartnerDashboard {...props} />} />
              </Routes>
            )}
          {isAuthenticated &&
            userRecord?.status &&
            userRecord?.status === 'active' &&
            userRecord?.userType === Entity.Driver && (
              <Routes>
                <Route path="/*" element={<DriverDashboard {...props} />} />
              </Routes>
            )}
        </>
      )}
      <MobileAppPrompt />
    </DashboardLayout>
  );
}

export default Dashboard;

interface DashboardLayoutProps {
  children: React.ReactNode;
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  screenView: string;
  showSideNav: boolean;
  setShowSideNav: (show: boolean) => void;
  uncategorizedTransactionsCount: number;
  isLoadingUncategorizedTransactionsCount: boolean;
}

export function DashboardLayout({
  children,
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  screenView,
  showSideNav,
  setShowSideNav,
  uncategorizedTransactionsCount,
  isLoadingUncategorizedTransactionsCount,
}: DashboardLayoutProps) {
  return (
    <div className={`flex min-h-screen bg-primarybg`}>
      <div
        className={`${
          screenView === 'tablet' || screenView === 'mobile'
            ? 'flex min-h-screen w-full flex-row items-center justify-start align-middle'
            : 'flex h-screen w-full flex-row justify-start overflow-hidden'
        }`}
      >
        <SideNav
          userType={userType}
          setCurrentTab={setCurrentTab}
          currentTab={currentTab}
          currentPage={currentPage}
          subTab={subTab}
          setSubTab={setSubTab}
          showSideNav={showSideNav}
          setShowSideNav={setShowSideNav}
          uncategorizedTransactionsCount={uncategorizedTransactionsCount}
          isLoadingUncategorizedTransactionsCount={
            isLoadingUncategorizedTransactionsCount
          }
        />
        <div
          className={`flex h-full w-full flex-1 flex-col items-center overflow-hidden ${screenView === 'desktop' && 'p-[16px]'} align-middle`}
        >
          <TopBar
            userType={userType}
            currentPage={currentPage}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            subTab={subTab}
            setSubTab={setSubTab}
            showSideNav={showSideNav}
            setShowSideNav={setShowSideNav}
            currSubPage={null}
            setCurrSubPage={() => {}}
          />
          {children}
        </div>
      </div>
    </div>
  );
}
